if (!customElements.get("custom-product-as-variant")) {
    class CustomProductAsVariant extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", this.onVariantClick.bind(this));
  }

  onVariantClick(event) {
    event.preventDefault();

    // -------------------------
    // ACTIVE STATE — always update immediately for visual feedback
    // -------------------------
    const container = this.closest(".variant-container") || this.parentElement;
    if (container) {
      container.querySelectorAll(".thumbnail-swatch")
        .forEach(el => {
          el.classList.remove("active");
          el.classList.remove("is-selected");
        });
    }
    this.classList.add("active");
    this.classList.add("is-selected");

    // =========================
    // CHECK 1: Are we inside a Quick View modal?
    // =========================
    const quickBuyModal = this.closest("quick-buy-modal") || this.closest(".quick-buy-modal");

    if (quickBuyModal) {
      // -------------------------
      // 1. UPDATE IMAGE INSTANTLY FOR ZERO-FLICKER FEEDBACK
      // -------------------------
      const modalImgs = quickBuyModal.querySelectorAll(".product-gallery__media img, .product-gallery img, .product-gallery__carousel img");
      const newImageSrc = this.dataset.image;

      if (modalImgs.length > 0 && newImageSrc) {
        modalImgs.forEach(img => {
          img.src = newImageSrc;
          img.removeAttribute("srcset");
        });
      }

      // -------------------------
      // 2. UPDATE HIDDEN VARIANT ID & PRODUCT FORM
      // -------------------------
      const form = quickBuyModal.querySelector("form");
      const variantInput = form ? form.querySelector("input[name='id']") : quickBuyModal.querySelector("input[name='id']");
      if (variantInput && this.dataset.variantId) {
        variantInput.value = this.dataset.variantId;
        variantInput.dispatchEvent(new Event("change", { bubbles: true }));
        variantInput.dispatchEvent(new Event("input", { bubbles: true }));
      }

      if (this.dataset.variantUrl) {
        const urlParts = this.dataset.variantUrl.split('/products/');
        if (urlParts.length > 1) {
          const handle = urlParts[1].split('?')[0];
          quickBuyModal.setAttribute('handle', handle + (this.dataset.variantId ? `?variant=${this.dataset.variantId}` : ''));
        }
      }

      // -------------------------
      // 3. FETCH FULL QUICK-BUY CONTENT FOR SEAMLESS SWAP
      // -------------------------
      if (this.dataset.variantUrl) {
        const fetchUrl = this.dataset.variantUrl;
        fetch(fetchUrl)
          .then(res => res.text())
          .then(htmlText => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlText, "text/html");
            const quickBuyTemplate = doc.getElementById("quick-buy-content");

            if (quickBuyTemplate && quickBuyTemplate.content) {
              const newContent = quickBuyTemplate.content.cloneNode(true);
              Array.from(newContent.querySelectorAll("noscript")).forEach(n => n.remove());

              quickBuyModal.replaceChildren(newContent);

              if (window.syncGokwikButtons) {
                window.syncGokwikButtons(quickBuyModal);
              }

              if (window.initQuickBuyEdd) {
                window.initQuickBuyEdd(quickBuyModal);
              }

              document.dispatchEvent(new CustomEvent("shopify:section:load", {
                detail: { sectionId: 'quick-buy' },
                target: quickBuyModal
              }));
              document.dispatchEvent(new CustomEvent("variant:change", {
                detail: {
                  variant: {
                    id: this.dataset.variantId,
                    product_id: this.dataset.selectedProductId
                  }
                }
              }));
            }
          })
          .catch(err => {
            console.error("Quick Buy variant swap error:", err);
          });
      }

      return;
    }

    // =========================
    // CHECK 2: Are we inside a product card? (Collection page, drawer, etc.)
    // =========================
    const productCard = this.closest(".product-card") || this.closest("product-card");

    if (productCard) {
      // =========================
      // PRODUCT CARD CONTEXT (Collection, Similar Products Drawer, etc.)
      // Fetch full product card HTML to update gallery, price, title, etc.
      // =========================
      
      // -------------------------
      // 1. UPDATE IMAGE INSTANTLY FOR FEEDBACK
      // -------------------------
      const img = productCard.querySelector(".product-card__image");
      const newImageSrc = this.dataset.image;

      if (img && newImageSrc) {
        img.src = newImageSrc;
        img.removeAttribute("srcset");
      }

      // Update discount badge instantly if available
      const discountBadge = productCard.querySelector("on-sale-badge");
      if (discountBadge && this.dataset.discountPercentage !== undefined) {
        const discount = parseInt(this.dataset.discountPercentage, 10);
        const minDiscount = parseInt(discountBadge.dataset.minDiscount || '0', 10);
        const textSpan = discountBadge.querySelector('.badge__text') || discountBadge;
        if (!isNaN(discount) && discount >= minDiscount && discount > 0) {
          textSpan.textContent = discount + '% OFF';
          discountBadge.removeAttribute('hidden');
          discountBadge.style.setProperty('display', '', 'important');
        } else {
          discountBadge.setAttribute('hidden', '');
          discountBadge.style.setProperty('display', 'none', 'important');
        }
      }

      // -------------------------
      // 2. FETCH FULL CARD FOR SEAMLESS SWAP
      // -------------------------
      productCard.classList.add("is-loading");
      productCard.style.pointerEvents = 'none';

      const sectionUrl = this.dataset.variantUrl + (this.dataset.variantUrl.includes('?') ? '&' : '?') + 'section_id=api-product-card';

      fetch(sectionUrl)
        .then(res => res.text())
        .then(htmlText => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(htmlText, "text/html");
          const newCard = doc.querySelector("product-card");

          if (!newCard) {
            throw new Error("Failed to parse new product card.");
          }

          // Swap function
          const swapCard = () => {
            productCard.replaceWith(newCard);
            if (window.pcInitCarousel) {
              const newCarousel = newCard.querySelector('[data-product-carousel]');
              if (newCarousel) {
                delete newCarousel.dataset.carouselInit;
                window.pcInitCarousel(newCarousel);
              }
            } else if (window.pcInitAll) {
              window.pcInitAll();
            }
          };

          // Preload primary image to prevent blank flash
          const newPrimaryImg = newCard.querySelector('.product-card__image--primary') || newCard.querySelector('img');
          
          if (newPrimaryImg && (newPrimaryImg.src || newPrimaryImg.srcset || newPrimaryImg.dataset.src)) {
            const tempImg = new Image();
            tempImg.onload = swapCard;
            tempImg.onerror = swapCard;
            
            if (newPrimaryImg.srcset) {
               tempImg.srcset = newPrimaryImg.srcset;
               if (newPrimaryImg.sizes) tempImg.sizes = newPrimaryImg.sizes;
            } else {
               tempImg.src = newPrimaryImg.src || newPrimaryImg.currentSrc || newPrimaryImg.dataset.src;
            }
          } else {
            swapCard();
          }
        })
        .catch(err => {
          console.error("Product as Variant: card update error", err);
          productCard.classList.remove("is-loading");
          productCard.style.pointerEvents = '';
        });

      return; // Don't proceed to PDP fetch
    }

    // =========================
    // PRODUCT PAGE (PDP) — update main product section and Judge.me reviews
    // =========================
    const isProductPage = document.querySelector("safe-sticky.product-info");

    if (isProductPage) {
      fetch(this.dataset.variantUrl)
        .then(res => res.text())
        .then(htmlText => {
          const html = new DOMParser().parseFromString(htmlText, "text/html");

          const currentMainSection = document.querySelector(".shopify-section--main-product") || 
                                       document.querySelector("safe-sticky.product-info")?.closest(".shopify-section");
          const newMainSection = html.querySelector(".shopify-section--main-product") || 
                                 html.querySelector("safe-sticky.product-info")?.closest(".shopify-section");

          if (currentMainSection && newMainSection) {
            currentMainSection.replaceWith(newMainSection);

            // Re-sync Gokwik buttons if available
            if (window.syncGokwikButtons) {
              window.syncGokwikButtons(newMainSection);
            }

            // Dispatch shopify section load event for theme components / app scripts
            document.dispatchEvent(new CustomEvent("shopify:section:load", {
              detail: { sectionId: newMainSection.id },
              target: newMainSection
            }));
          } else {
            // Fallback if main product section container isn't directly matched
            const main = document.querySelector("#main");
            const newMain = html.querySelector("#main");

            if (main && newMain) {
              const currentProductInfo = main.querySelector(".product-info") || main.querySelector("safe-sticky");
              const newProductInfo = newMain.querySelector(".product-info") || newMain.querySelector("safe-sticky");

              if (currentProductInfo && newProductInfo) {
                const currentContainer = currentProductInfo.closest(".shopify-section") || currentProductInfo.parentElement;
                const newContainer = newProductInfo.closest(".shopify-section") || newProductInfo.parentElement;
                if (currentContainer && newContainer) {
                  currentContainer.replaceWith(newContainer);
                }
              }
            }
          }

          // Extract new Product ID & Variant ID
          const newProductId = html.querySelector(".jdgm-widget[data-id], .jdgm-rev-widg[data-id]")?.getAttribute("data-id") ||
                               newMainSection?.querySelector("[data-product-id]")?.dataset.productId ||
                               newMainSection?.querySelector(".jdgm-widget[data-id]")?.getAttribute("data-id");

          const newVariantId = this.dataset.variantId || 
                               newMainSection?.querySelector('input[name="id"]')?.value;

          // ✅ IMPORTANT: update URL
          window.history.pushState({}, "", this.dataset.variantUrl);

          // Update Judge.me reviews widget dynamically with new Product & Variant IDs after main section updates
          if (window.updateJudgemeWidget) {
            window.updateJudgemeWidget(newProductId, newVariantId);
          }

          // Sync PDP Wishlist buttons for newly swapped product
          if (window.syncPdpWishlist) {
            window.syncPdpWishlist(newProductId);
          }
        })
        .catch(err => {
          console.error("PDP variant swap error:", err);
        });

      return;
    }
  }
}

// Global helper for updating Judge.me widget on variant change
if (typeof window.updateJudgemeWidget !== "function") {
  window.updateJudgemeWidget = function(productId, variantId) {
    // 1. Update data attributes across all intact Judge.me widgets & badges on the page
    const widgets = document.querySelectorAll(".jdgm-widget, .jdgm-rev-widg, .jdgm-preview-badge, .jdgm-prev-badge");

    widgets.forEach(el => {
      if (productId) {
        el.setAttribute("data-id", productId);
        el.setAttribute("data-product-id", productId);
      }
      if (variantId) {
        el.setAttribute("data-variant-id", variantId);
      }
    });

    // 2. Trigger Judge.me's customization & reload APIs to transform raw markup into styled widget cards
    const triggerJudgeme = () => {
      document.querySelectorAll(".jdgm-widget, .jdgm-review-widget, .jdgm-rev-widg, .jdgm-preview-badge, .jdgm-prev-badge").forEach(el => {
        if (window.jdgm && typeof window.jdgm.customizeElement === "function") {
          try { window.jdgm.customizeElement(el); } catch (e) {}
        }
      });

      if (window.jdgm) {
        if (typeof window.jdgm.reloadWidgets === "function") {
          try { window.jdgm.reloadWidgets({ product_id: productId, variant_id: variantId }); } catch (e) {}
        }
        if (typeof window.jdgm.batchRender === "function") {
          try { window.jdgm.batchRender(); } catch (e) {}
        }
        if (typeof window.jdgm.initializeWidgets === "function") {
          try { window.jdgm.initializeWidgets(); } catch (e) {}
        }
      }
      if (window.JDGM && typeof window.JDGM.reloadWidgets === "function") {
        try { window.JDGM.reloadWidgets(); } catch (e) {}
      }
      if (typeof window.jdgmReInit === "function") {
        try { window.jdgmReInit(); } catch (e) {}
      }

      if (typeof window.handleJudgemeZeroReviews === "function") {
        try { window.handleJudgemeZeroReviews(); } catch (e) {}
      }

      document.dispatchEvent(new CustomEvent("jdgm.doneSetup"));
    };

    triggerJudgeme();
    setTimeout(triggerJudgeme, 150);
    setTimeout(triggerJudgeme, 400);
  };

  document.addEventListener("variant:change", function(event) {
    const variant = event.detail?.variant;
    if (variant && (variant.id || variant.product_id)) {
      window.updateJudgemeWidget(variant.product_id || null, variant.id);
    }
  });
}

// Dynamic handler to hide 0-review widgets & rating containers
if (typeof window.handleJudgemeZeroReviews !== "function") {
  window.handleJudgemeZeroReviews = function() {
    const badges = document.querySelectorAll(".jdgm-prev-badge, .jdgm-preview-badge, .jdgm-widget");
    badges.forEach(badge => {
      const numReviewsAttr = badge.getAttribute("data-number-of-reviews");
      const avgRatingAttr = badge.getAttribute("data-average-rating");
      const textEl = badge.querySelector(".jdgm-prev-badge__text");
      const textContent = textEl ? textEl.textContent.trim().toLowerCase() : "";

      const isZero = numReviewsAttr === "0" || 
                     numReviewsAttr === "" || 
                     avgRatingAttr === "0" || 
                     avgRatingAttr === "0.0" || 
                     avgRatingAttr === "0.00" || 
                     textContent === "no reviews" || 
                     textContent === "0 reviews";

      if (isZero) {
        badge.style.setProperty("display", "none", "important");
        const mobileContainer = badge.closest(".pdp-header-mobile__rating");
        if (mobileContainer) {
          let hasVisibleChild = false;
          Array.from(mobileContainer.children).forEach(child => {
            if (child.style.display !== "none" && getComputedStyle(child).display !== "none") {
              hasVisibleChild = true;
            }
          });
          if (!hasVisibleChild) {
            mobileContainer.style.setProperty("display", "none", "important");
            mobileContainer.style.setProperty("margin", "0", "important");
            mobileContainer.style.setProperty("padding", "0", "important");
          }
        }
      } else if (numReviewsAttr && parseInt(numReviewsAttr, 10) > 0) {
        badge.style.removeProperty("display");
        const mobileContainer = badge.closest(".pdp-header-mobile__rating");
        if (mobileContainer) {
          mobileContainer.style.removeProperty("display");
          mobileContainer.style.removeProperty("margin");
          mobileContainer.style.removeProperty("padding");
        }
      }
    });
  };

  document.addEventListener("DOMContentLoaded", window.handleJudgemeZeroReviews);
  document.addEventListener("jdgm.doneSetup", window.handleJudgemeZeroReviews);

  if (typeof window.MutationObserver !== "undefined") {
    try {
      const observer = new MutationObserver(window.handleJudgemeZeroReviews);
      const target = document.querySelector(".pdp-header-mobile__rating") || document.body;
      if (target) {
        observer.observe(target, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-number-of-reviews", "data-average-rating"] });
      }
    } catch (e) {}
  }
}

customElements.define("custom-product-as-variant", CustomProductAsVariant);
}